/**
 * US State Sales Tax Rates (2024)
 * Used for Car Calculator and Housing Calculator
 * Rates include average local taxes where applicable
 */

export interface StateTaxInfo {
  code: string;
  name: string;
  salesTax: number;        // Combined state + avg local sales tax %
  propertyTaxRate: number; // Effective property tax rate % of home value
  hasStateTax: boolean;    // Whether state has income tax (for reference)
}

export const US_STATE_TAX_RATES: Record<string, StateTaxInfo> = {
  'AL': { code: 'AL', name: 'Alabama', salesTax: 9.24, propertyTaxRate: 0.41, hasStateTax: true },
  'AK': { code: 'AK', name: 'Alaska', salesTax: 1.76, propertyTaxRate: 1.19, hasStateTax: false },
  'AZ': { code: 'AZ', name: 'Arizona', salesTax: 8.40, propertyTaxRate: 0.62, hasStateTax: true },
  'AR': { code: 'AR', name: 'Arkansas', salesTax: 9.51, propertyTaxRate: 0.62, hasStateTax: true },
  'CA': { code: 'CA', name: 'California', salesTax: 8.68, propertyTaxRate: 0.74, hasStateTax: true },
  'CO': { code: 'CO', name: 'Colorado', salesTax: 7.77, propertyTaxRate: 0.51, hasStateTax: true },
  'CT': { code: 'CT', name: 'Connecticut', salesTax: 6.35, propertyTaxRate: 2.14, hasStateTax: true },
  'DE': { code: 'DE', name: 'Delaware', salesTax: 0.00, propertyTaxRate: 0.57, hasStateTax: true },
  'FL': { code: 'FL', name: 'Florida', salesTax: 7.02, propertyTaxRate: 0.89, hasStateTax: false },
  'GA': { code: 'GA', name: 'Georgia', salesTax: 7.35, propertyTaxRate: 0.92, hasStateTax: true },
  'HI': { code: 'HI', name: 'Hawaii', salesTax: 4.44, propertyTaxRate: 0.28, hasStateTax: true },
  'ID': { code: 'ID', name: 'Idaho', salesTax: 6.02, propertyTaxRate: 0.69, hasStateTax: true },
  'IL': { code: 'IL', name: 'Illinois', salesTax: 8.81, propertyTaxRate: 2.27, hasStateTax: true },
  'IN': { code: 'IN', name: 'Indiana', salesTax: 7.00, propertyTaxRate: 0.85, hasStateTax: true },
  'IA': { code: 'IA', name: 'Iowa', salesTax: 6.94, propertyTaxRate: 1.57, hasStateTax: true },
  'KS': { code: 'KS', name: 'Kansas', salesTax: 8.70, propertyTaxRate: 1.41, hasStateTax: true },
  'KY': { code: 'KY', name: 'Kentucky', salesTax: 6.00, propertyTaxRate: 0.86, hasStateTax: true },
  'LA': { code: 'LA', name: 'Louisiana', salesTax: 9.55, propertyTaxRate: 0.55, hasStateTax: true },
  'ME': { code: 'ME', name: 'Maine', salesTax: 5.50, propertyTaxRate: 1.36, hasStateTax: true },
  'MD': { code: 'MD', name: 'Maryland', salesTax: 6.00, propertyTaxRate: 1.09, hasStateTax: true },
  'MA': { code: 'MA', name: 'Massachusetts', salesTax: 6.25, propertyTaxRate: 1.23, hasStateTax: true },
  'MI': { code: 'MI', name: 'Michigan', salesTax: 6.00, propertyTaxRate: 1.54, hasStateTax: true },
  'MN': { code: 'MN', name: 'Minnesota', salesTax: 7.49, propertyTaxRate: 1.12, hasStateTax: true },
  'MS': { code: 'MS', name: 'Mississippi', salesTax: 7.07, propertyTaxRate: 0.81, hasStateTax: true },
  'MO': { code: 'MO', name: 'Missouri', salesTax: 8.29, propertyTaxRate: 0.97, hasStateTax: true },
  'MT': { code: 'MT', name: 'Montana', salesTax: 0.00, propertyTaxRate: 0.84, hasStateTax: true },
  'NE': { code: 'NE', name: 'Nebraska', salesTax: 6.94, propertyTaxRate: 1.73, hasStateTax: true },
  'NV': { code: 'NV', name: 'Nevada', salesTax: 8.23, propertyTaxRate: 0.60, hasStateTax: false },
  'NH': { code: 'NH', name: 'New Hampshire', salesTax: 0.00, propertyTaxRate: 2.18, hasStateTax: false },
  'NJ': { code: 'NJ', name: 'New Jersey', salesTax: 6.63, propertyTaxRate: 2.49, hasStateTax: true },
  'NM': { code: 'NM', name: 'New Mexico', salesTax: 7.83, propertyTaxRate: 0.80, hasStateTax: true },
  'NY': { code: 'NY', name: 'New York', salesTax: 8.52, propertyTaxRate: 1.72, hasStateTax: true },
  'NC': { code: 'NC', name: 'North Carolina', salesTax: 6.98, propertyTaxRate: 0.84, hasStateTax: true },
  'ND': { code: 'ND', name: 'North Dakota', salesTax: 6.96, propertyTaxRate: 0.98, hasStateTax: true },
  'OH': { code: 'OH', name: 'Ohio', salesTax: 7.24, propertyTaxRate: 1.56, hasStateTax: true },
  'OK': { code: 'OK', name: 'Oklahoma', salesTax: 8.98, propertyTaxRate: 0.90, hasStateTax: true },
  'OR': { code: 'OR', name: 'Oregon', salesTax: 0.00, propertyTaxRate: 0.97, hasStateTax: true },
  'PA': { code: 'PA', name: 'Pennsylvania', salesTax: 6.34, propertyTaxRate: 1.58, hasStateTax: true },
  'RI': { code: 'RI', name: 'Rhode Island', salesTax: 7.00, propertyTaxRate: 1.63, hasStateTax: true },
  'SC': { code: 'SC', name: 'South Carolina', salesTax: 7.43, propertyTaxRate: 0.57, hasStateTax: true },
  'SD': { code: 'SD', name: 'South Dakota', salesTax: 6.40, propertyTaxRate: 1.31, hasStateTax: false },
  'TN': { code: 'TN', name: 'Tennessee', salesTax: 9.55, propertyTaxRate: 0.71, hasStateTax: false },
  'TX': { code: 'TX', name: 'Texas', salesTax: 8.20, propertyTaxRate: 1.80, hasStateTax: false },
  'UT': { code: 'UT', name: 'Utah', salesTax: 7.19, propertyTaxRate: 0.63, hasStateTax: true },
  'VT': { code: 'VT', name: 'Vermont', salesTax: 6.24, propertyTaxRate: 1.90, hasStateTax: true },
  'VA': { code: 'VA', name: 'Virginia', salesTax: 5.75, propertyTaxRate: 0.82, hasStateTax: true },
  'WA': { code: 'WA', name: 'Washington', salesTax: 9.29, propertyTaxRate: 0.98, hasStateTax: false },
  'WV': { code: 'WV', name: 'West Virginia', salesTax: 6.55, propertyTaxRate: 0.58, hasStateTax: true },
  'WI': { code: 'WI', name: 'Wisconsin', salesTax: 5.43, propertyTaxRate: 1.85, hasStateTax: true },
  'WY': { code: 'WY', name: 'Wyoming', salesTax: 5.36, propertyTaxRate: 0.61, hasStateTax: false },
  'DC': { code: 'DC', name: 'District of Columbia', salesTax: 6.00, propertyTaxRate: 0.56, hasStateTax: true },
};

// Canadian provinces for reference
export const CA_PROVINCE_TAX_RATES: Record<string, { salesTax: number; name: string }> = {
  'ON': { name: 'Ontario', salesTax: 13 },
  'BC': { name: 'British Columbia', salesTax: 12 },
  'AB': { name: 'Alberta', salesTax: 5 },
  'QC': { name: 'Quebec', salesTax: 14.975 },
  'MB': { name: 'Manitoba', salesTax: 12 },
  'SK': { name: 'Saskatchewan', salesTax: 11 },
  'NS': { name: 'Nova Scotia', salesTax: 15 },
  'NB': { name: 'New Brunswick', salesTax: 15 },
  'NL': { name: 'Newfoundland and Labrador', salesTax: 15 },
  'PE': { name: 'Prince Edward Island', salesTax: 15 },
  'NT': { name: 'Northwest Territories', salesTax: 5 },
  'YT': { name: 'Yukon', salesTax: 5 },
  'NU': { name: 'Nunavut', salesTax: 5 },
};

/**
 * Get average US sales tax rate (weighted by population)
 */
export function getAverageUSSalesTax(): number {
  return 7.12; // Population-weighted average
}

/**
 * Get sales tax for a specific US state
 */
export function getStateSalesTax(stateCode: string): number {
  return US_STATE_TAX_RATES[stateCode]?.salesTax ?? getAverageUSSalesTax();
}

/**
 * Get property tax rate for a specific US state
 */
export function getStatePropertyTax(stateCode: string): number {
  return US_STATE_TAX_RATES[stateCode]?.propertyTaxRate ?? 1.1; // US average
}

/**
 * States with no sales tax (useful for car purchases)
 */
export const NO_SALES_TAX_STATES = ['DE', 'MT', 'NH', 'OR'];

/**
 * States with no income tax (relevant for retirement planning)
 */
export const NO_INCOME_TAX_STATES = ['AK', 'FL', 'NV', 'NH', 'SD', 'TN', 'TX', 'WA', 'WY'];
